import React from 'react';
import { SSK_OFFICIAL_LOGO } from '../constants/brand';
import { cn } from '../lib/utils';

interface BrandLogoProps {
  className?: string;
  imgClassName?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  showText?: boolean;
  hideTextOnMobile?: boolean;
  lightText?: boolean;
  subtitle?: string;
  subtitleClassName?: string;
  textClassName?: string;
  onClick?: () => void;
}

export function BrandLogo({ 
  className, 
  imgClassName,
  size = 'md', 
  showText = false, 
  hideTextOnMobile = false,
  lightText = true,
  subtitle,
  subtitleClassName,
  textClassName,
  onClick
}: BrandLogoProps) {
  const containerSizeMap = {
    xs: 'w-7 h-7 rounded-lg p-0.5',
    sm: 'w-9 h-9 sm:w-10 sm:h-10 rounded-xl p-1',
    md: 'w-11 h-11 rounded-[12px] p-1.5',
    lg: 'w-16 h-16 rounded-[18px] p-2',
    xl: 'w-24 h-24 rounded-[24px] p-2.5',
    '2xl': 'w-32 h-32 rounded-[32px] p-3',
  };

  return (
    <div 
      onClick={onClick}
      className={cn("flex items-center gap-2.5 sm:gap-3 shrink-0 select-none min-w-0", onClick && "cursor-pointer", className)}
    >
      <div className={cn(
        "bg-white flex items-center justify-center shrink-0 overflow-hidden transition-transform duration-300 hover:scale-[1.02]", 
        lightText ? "border border-white/20 shadow-lg shadow-black/10" : "border border-slate-200 shadow-sm",
        containerSizeMap[size]
      )}>
        <img 
          src={size === 'xs' || size === 'sm' || size === 'md' ? '/ssk-logo-64.webp' : SSK_OFFICIAL_LOGO} 
          srcSet="/ssk-logo-32.webp 32w, /ssk-logo-64.webp 64w, /ssk-logo-128.webp 128w, /ssk-logo-256.webp 256w"
          sizes="(max-width: 640px) 36px, (max-width: 1024px) 44px, 64px"
          alt="SSK Business Network Logo" 
          width={44}
          height={44}
          decoding="async"
          className={cn("w-full h-full object-contain drop-shadow-sm", imgClassName)}
          referrerPolicy="no-referrer"
          loading="eager"
        />
      </div>

      {showText && (
        <div className={cn("flex flex-col min-w-0", hideTextOnMobile && "hidden sm:flex")}>
          <span className={cn(
            "font-black tracking-wider leading-none uppercase truncate",
            size === 'xs' ? "text-[11px]" :
            size === 'sm' ? "text-[13px] sm:text-sm lg:text-sm" :
            size === 'md' ? "text-sm sm:text-base" :
            size === 'lg' ? "text-lg sm:text-xl" :
            "text-2xl",
            lightText ? "text-white" : "text-[#0F2040]",
            textClassName
          )}>
            SSK <span className="text-primary font-extrabold">BUSINESS NETWORK</span>
          </span>
          {subtitle && (
            <span className={cn(
              "font-extrabold tracking-[0.2em] uppercase mt-0.5 leading-tight truncate",
              size === 'xs' || size === 'sm' ? "text-[7px] sm:text-[8px]" : "text-[8px] sm:text-[9px]",
              lightText ? "text-neutral-400" : "text-neutral-500",
              subtitleClassName
            )}>
              {subtitle}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
